import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/hooks/use-toast';
import {
  useAllTickets,
  useTicketMessages,
  useSendMessage,
  useUpdateTicketStatus,
  useAssignTicket,
  useAvailableAgents,
  useMarkSolution,
} from '@/hooks/useTickets';
import { uploadTicketAttachment } from '@/hooks/useTickets';
import { useAuth } from '@/contexts/AuthContext';
import {
  Ticket, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  MessageSquare,
  Search,
  Headphones,
  Loader2,
  XCircle,
  User,
  UserCheck,
  ArrowUpDown,
  Eye,
  Reply,
  FileText,
  Image as ImageIcon,
  Download,
  Paperclip,
  X,
  ShieldCheck,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { OrganizationFilter, useOrganizationFilter } from '@/components/admin/OrganizationFilter';
import { useOrgFilterStore } from '@/hooks/useOrgFilterStore';

type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
type TicketCategory = 'technical' | 'access' | 'content' | 'billing' | 'other';

const categoryLabels: Record<TicketCategory, string> = {
  technical: 'Problema Técnico',
  access: 'Acceso a Plataforma',
  content: 'Contenido',
  billing: 'Facturación',
  other: 'Otro'
};

const statusLabels: Record<string, string> = {
  open: 'Abierto',
  in_progress: 'En Proceso',
  resolved: 'Resuelto',
  closed: 'Cerrado'
};

const statusColors: Record<string, string> = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-muted text-muted-foreground border-border'
};

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'open': return <AlertCircle className="w-4 h-4" />;
    case 'in_progress': return <Loader2 className="w-4 h-4 animate-spin" />;
    case 'resolved': return <CheckCircle2 className="w-4 h-4" />;
    case 'closed': return <XCircle className="w-4 h-4" />;
    default: return <AlertCircle className="w-4 h-4" />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function TicketManagementPage() {
  const { user } = useAuth();
  const [selectedOrg, setSelectedOrg] = useOrgFilterStore();
  const { isSuperAdmin, showAllOrgs, getOrgName } = useOrganizationFilter(selectedOrg);

  const { data: tickets = [], isLoading } = useAllTickets(selectedOrg);
  const { data: agents = [] } = useAvailableAgents();
  const updateStatus = useUpdateTicketStatus();
  const assignTicket = useAssignTicket();
  const sendMessage = useSendMessage();
  const markSolution = useMarkSolution();

  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<TicketStatus | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<TicketCategory | 'all'>('all');
  const [filterAgent, setFilterAgent] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const selectedTicket = tickets.find(t => t.id === selectedTicketId) ?? null;
  const { data: messages = [] } = useTicketMessages(isDetailOpen ? selectedTicketId : null);

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length
  };

  const filteredTickets = tickets
    .filter(ticket => {
      const matchesSearch = 
        ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.user_name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ticket.user_email ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
      const matchesCategory = filterCategory === 'all' || ticket.category === filterCategory;
      const matchesAgent = filterAgent === 'all' || ticket.assigned_to === filterAgent || (filterAgent === 'unassigned' && !ticket.assigned_to);
      return matchesSearch && matchesStatus && matchesCategory && matchesAgent;
    })
    .sort((a, b) => {
      return sortOrder === 'desc'
        ? new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        : new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
    });

  const handleViewTicket = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setIsDetailOpen(true);
  };

  const handleStatusChange = async (ticketId: string, newStatus: TicketStatus) => {
    try {
      await updateStatus.mutateAsync({ ticketId, status: newStatus });
      toast({ title: "Estado actualizado", description: `Ticket marcado como ${statusLabels[newStatus]}` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleAssignAgent = async (ticketId: string, agentId: string | null) => {
    try {
      await assignTicket.mutateAsync({ ticketId, agentId });
      const agentName = agentId ? agents.find(a => a.id === agentId)?.name : null;
      toast({
        title: agentId ? "Agente asignado" : "Asignación removida",
        description: agentId ? `Asignado a ${agentName}` : "Sin agente asignado"
      });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleSendReply = async () => {
    if ((!replyMessage.trim() && replyFiles.length === 0) || !selectedTicketId || !user) return;
    try {
      setIsUploading(true);
      let attachmentUrls: { name: string; url: string; type: string; size: number }[] = [];
      if (replyFiles.length > 0) {
        attachmentUrls = await Promise.all(replyFiles.map(f => uploadTicketAttachment(f, user.id)));
      }
      await sendMessage.mutateAsync({
        ticketId: selectedTicketId,
        message: replyMessage || '(Archivo adjunto)',
        senderType: 'agent',
        attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined,
      });
      setReplyMessage('');
      setReplyFiles([]);
      toast({ title: "Respuesta enviada", description: "El usuario ha sido notificado." });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setReplyFiles(prev => [...prev, ...newFiles]);
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Headphones className="w-8 h-8 text-primary" />
            Gestión de Tickets
          </h1>
          <p className="text-muted-foreground mt-1">
            {isSuperAdmin && showAllOrgs 
              ? 'Tickets de todas las organizaciones' 
              : `Tickets de ${getOrgName()}`}
          </p>
        </div>
        <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20"><Ticket className="w-5 h-5 text-primary" /></div>
            <div><p className="text-2xl font-bold text-foreground">{stats.total}</p><p className="text-xs text-muted-foreground">Total</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-blue-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20"><AlertCircle className="w-5 h-5 text-blue-400" /></div>
            <div><p className="text-2xl font-bold text-blue-400">{stats.open}</p><p className="text-xs text-muted-foreground">Abiertos</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-yellow-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20"><Clock className="w-5 h-5 text-yellow-400" /></div>
            <div><p className="text-2xl font-bold text-yellow-400">{stats.inProgress}</p><p className="text-xs text-muted-foreground">En Proceso</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-green-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20"><CheckCircle2 className="w-5 h-5 text-green-400" /></div>
            <div><p className="text-2xl font-bold text-green-400">{stats.resolved}</p><p className="text-xs text-muted-foreground">Resueltos</p></div>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted"><XCircle className="w-5 h-5 text-muted-foreground" /></div>
            <div><p className="text-2xl font-bold text-foreground">{stats.closed}</p><p className="text-xs text-muted-foreground">Cerrados</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Buscar por título, usuario o email..." className="pl-9" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as TicketStatus | 'all')}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v as TicketCategory | 'all')}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Agente" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los agentes</SelectItem>
                  <SelectItem value="unassigned">Sin asignar</SelectItem>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className="bg-card/50 border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Tickets ({filteredTickets.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Agente</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>
                    <Button variant="ghost" size="sm" className="gap-1 -ml-3" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}>
                      Última actualización
                      <ArrowUpDown className="w-3 h-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Ticket className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-muted-foreground">No se encontraron tickets</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTickets.map(ticket => (
                    <TableRow key={ticket.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div>
                          <p className="font-medium line-clamp-1">{ticket.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{ticket.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{ticket.user_name}</p>
                            <p className="text-xs text-muted-foreground">{ticket.user_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[ticket.category as TicketCategory] ?? ticket.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {ticket.assigned_to ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                              <UserCheck className="w-3 h-3 text-green-400" />
                            </div>
                            <span className="text-sm">{ticket.assigned_name}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">Sin asignar</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${statusColors[ticket.status] ?? ''}`}>
                          <StatusIcon status={ticket.status} />
                          <span className="ml-1">{statusLabels[ticket.status] ?? ticket.status}</span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(ticket.updated_at).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewTicket(ticket.id)}>
                          <Eye className="w-4 h-4 mr-1" /> Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              {selectedTicket?.title}
            </DialogTitle>
            <DialogDescription>{selectedTicket?.description}</DialogDescription>
          </DialogHeader>

          {selectedTicket && (
            <div className="flex-1 overflow-hidden flex flex-col gap-4">
              {/* Ticket Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Usuario</p>
                  <p className="font-medium">{selectedTicket.user_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedTicket.user_email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Categoría</p>
                  <Badge variant="outline" className="mt-1">
                    {categoryLabels[selectedTicket.category as TicketCategory] ?? selectedTicket.category}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Estado</p>
                  <Select 
                    value={selectedTicket.status} 
                    onValueChange={(v) => handleStatusChange(selectedTicket.id, v as TicketStatus)}
                  >
                    <SelectTrigger className="mt-1 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Agente Asignado</p>
                  <Select 
                    value={selectedTicket.assigned_to || 'unassigned'} 
                    onValueChange={(v) => handleAssignAgent(selectedTicket.id, v === 'unassigned' ? null : v)}
                  >
                    <SelectTrigger className="mt-1 h-8"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Sin asignar</SelectItem>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          <div className="flex items-center gap-2">
                            <UserCheck className="w-3 h-3" />
                            {agent.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 max-h-[300px] pr-4">
                <div className="space-y-4">
                  {messages.map(msg => {
                    const isSolution = selectedTicket.solution_message_id === msg.id;
                    return (
                      <div key={msg.id}>
                        {isSolution && (
                          <div className="flex items-center gap-2 mb-1 ml-1">
                            <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                            <span className="text-xs font-medium text-green-500">Solución</span>
                          </div>
                        )}
                        <div className={`flex ${msg.sender_type !== 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={cn(
                            "max-w-[80%] rounded-2xl px-4 py-3",
                            msg.sender_type !== 'user'
                              ? 'bg-primary text-primary-foreground rounded-br-md'
                              : 'bg-muted text-foreground rounded-bl-md',
                            isSolution && 'ring-2 ring-green-500/50 bg-green-500/10'
                          )}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium">
                                {msg.sender_type !== 'user' ? 'Soporte' : (msg.sender_name || 'Usuario')}
                              </span>
                            </div>
                            <p className="text-sm">{msg.message}</p>
                            {msg.attachments && Array.isArray(msg.attachments) && (msg.attachments as any[]).length > 0 && (
                              <div className="mt-2 space-y-2">
                                {(msg.attachments as any[]).map((att: any, idx: number) => (
                                  <div key={idx}>
                                    {att.type?.startsWith('image/') && (
                                      <a href={att.url} target="_blank" rel="noopener noreferrer" className="block mb-1">
                                        <img
                                          src={att.url}
                                          alt={att.name}
                                          className="max-w-[240px] max-h-[180px] rounded-lg border border-border/50 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                          loading="lazy"
                                        />
                                      </a>
                                    )}
                                    <a href={att.url} target="_blank" rel="noopener noreferrer"
                                      className={`flex items-center gap-2 text-xs p-2 rounded ${
                                        msg.sender_type !== 'user' ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20' : 'bg-background hover:bg-background/80'
                                      } transition-colors`}>
                                      {att.type?.startsWith('image/') ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                                      <span className="truncate max-w-[150px]">{att.name}</span>
                                      {att.size && <span className="opacity-60">({formatFileSize(att.size)})</span>}
                                      <Download className="w-3 h-3 ml-auto" />
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-1 gap-2">
                              <p className={`text-xs ${msg.sender_type !== 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                {new Date(msg.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} - {new Date(msg.created_at).toLocaleDateString('es-ES')}
                              </p>
                              {!isSolution && selectedTicket.status !== 'closed' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs gap-1 text-muted-foreground hover:text-green-500"
                                  onClick={() => {
                                    markSolution.mutate(
                                      { ticketId: selectedTicket.id, messageId: msg.id },
                                      { onSuccess: () => toast({ title: "Solución marcada", description: "El ticket ha sido marcado como resuelto." }) }
                                    );
                                  }}
                                  disabled={markSolution.isPending}
                                >
                                  <ShieldCheck className="w-3 h-3" />
                                  Marcar como solución
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4">
                  {replyFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {replyFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-1 bg-muted rounded px-2 py-1 text-xs">
                          <FileText className="w-3 h-3" />
                          <span className="truncate max-w-[120px]">{file.name}</span>
                          <button onClick={() => setReplyFiles(prev => prev.filter((_, i) => i !== idx))} className="ml-1 hover:text-destructive">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Textarea 
                      placeholder="Escribe tu respuesta..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div>
                      <input type="file" id="admin-reply-file" className="hidden" multiple onChange={handleReplyFileChange} />
                      <Button variant="outline" size="sm" onClick={() => document.getElementById('admin-reply-file')?.click()}>
                        <Paperclip className="w-4 h-4 mr-1" /> Adjuntar
                      </Button>
                    </div>
                    <Button onClick={handleSendReply} disabled={(!replyMessage.trim() && replyFiles.length === 0) || sendMessage.isPending || isUploading} className="gap-2">
                      {(sendMessage.isPending || isUploading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Reply className="w-4 h-4" />}
                      Enviar Respuesta
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
